import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Navigate, Link } from 'react-router-dom';

import {
  auth,
  getWarehouseItems,
  createWarehouseItem,
  updateWarehouseItemQuantity,
  renameWarehouseItem,
  updateWarehouseMinQuantity,
  deleteWarehouseItem,
} from '../../api';
import { ModalInfo } from '../../components/ModalInfo';
import WarehouseTable from './components/WarehouseTable';
import WarehouseFormModal from './components/WarehouseFormModal';
import WarehouseHistoryDrawer from './components/WarehouseHistoryDrawer';
import WarehouseQuantityModal from './components/WarehouseQuantityModal';
import WarehouseMinQuantityModal from './components/WarehouseMinQuantityModal';

const WarehousePage = () => {
  const [user] = useAuthState(auth);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [quantityItem, setQuantityItem] = useState(null);
  const [quantityActionType, setQuantityActionType] = useState('deposito');
  const [minQuantityItem, setMinQuantityItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteResultMessage, setDeleteResultMessage] = useState('');
  const [isDeleteResultOpen, setIsDeleteResultOpen] = useState(false);
  const toast = useToast();

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const {
    isOpen: isHistoryOpen,
    onOpen: onHistoryOpen,
    onClose: onHistoryClose,
  } = useDisclosure();

  const {
    isOpen: isQuantityOpen,
    onOpen: onQuantityOpen,
    onClose: onQuantityClose,
  } = useDisclosure();

  const {
    isOpen: isMinQuantityOpen,
    onOpen: onMinQuantityOpen,
    onClose: onMinQuantityClose,
  } = useDisclosure();

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getWarehouseItems();
      setItems(data);
    } catch (error) {
      console.error('Errore nel caricamento del magazzino', error);
      toast({
        title: 'Errore nel caricamento del magazzino',
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleCreate = async (values) => {
    try {
      await createWarehouseItem(values);
      onFormClose();
      await loadItems();
      toast({
        title: 'Prodotto creato',
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      console.error('Errore nella creazione del prodotto', error);
      toast({
        title: 'Errore nella creazione del prodotto',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleUpdateQuantity = async (id, delta) => {
    try {
      await updateWarehouseItemQuantity(id, delta);
      await loadItems();
    } catch (error) {
      console.error('Errore nell\'aggiornamento della quantità', error);
      toast({
        title: 'Errore nell\'aggiornamento della quantità',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleRename = async (id, name) => {
    try {
      await renameWarehouseItem(id, name);
      await loadItems();
      toast({
        title: 'Nome aggiornato',
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      console.error('Errore nel rinominare il prodotto', error);
      toast({
        title: 'Errore nel rinominare il prodotto',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleUpdateMinQuantity = async (id, minQuantity) => {
    try {
      await updateWarehouseMinQuantity(id, minQuantity);
      await loadItems();
      toast({
        title: 'Soglia minima aggiornata',
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      console.error('Errore nell\'aggiornamento della soglia minima', error);
      toast({
        title: 'Errore nell\'aggiornamento della soglia minima',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteItem) return;
    try {
      await deleteWarehouseItem(deleteItem.id);
      await loadItems();
      setDeleteResultMessage(`Prodotto "${deleteItem.name}" eliminato correttamente.`);
    } catch (error) {
      console.error('Errore nella cancellazione del prodotto', error);
      setDeleteResultMessage('Errore nella cancellazione del prodotto.');
    } finally {
      setDeleteItem(null);
      setIsDeleteResultOpen(true);
    }
  };

  const openCreateModal = () => {
    onFormOpen();
  };

  const openHistoryDrawer = (item) => {
    setHistoryItem(item);
    onHistoryOpen();
  };

  const openQuantityModal = (item, actionType) => {
    setQuantityItem(item);
    setQuantityActionType(actionType);
    onQuantityOpen();
  };

  const openMinQuantityModal = (item) => {
    setMinQuantityItem(item);
    onMinQuantityOpen();
  };

  const handleConfirmQuantityChange = async (item, delta) => {
    await handleUpdateQuantity(item.id, delta);
  };

  const handleConfirmMinQuantityChange = async (id, minQuantity) => {
    await handleUpdateMinQuantity(id, minQuantity);
  };

  // if (!user || (user.displayName !== 'admin' && user.displayName !== 'promoter'))
  //   return <Navigate to='/dashboard' replace={true} />;

  if (!user || (user.displayName !== 'admin' && user.displayName !== 'warehouse')) {
    return <Navigate to='/dashboard' replace={true} />;
  }

  return (
    <VStack w='100%' color='white' mb={10}>
      <Heading mt={5} mb={0} color='white'>
        Gestore Magazzino
      </Heading>
      <Text mb={1}>{process.env.REACT_APP_PROJECT_ID}</Text>
      <Button mb={4} as={Link} to='/dashboard'>
        Dashboard
      </Button>

      {/* <Box>
        <Text mb={4} fontSize='sm'>
          Gestisci le scorte di magazzino. Quando la quantità scende sotto la soglia minima impostata,
          viene eseguito un log per l&apos;invio di una mail di avviso.
        </Text>
      </Box> */}

      {isLoading ? (
        <Flex justify='center' align='center' minH='150px'>
          <Spinner size='lg' />
        </Flex>
      ) : (

        <Flex direction={{ base: 'column' }} gap={6} align='stretch'>
          <Box flex='1' minW={0}>
            <Flex justify='flex-end' mb={3}>
              <Button onClick={openCreateModal}>
                Nuovo prodotto
              </Button>
            </Flex>
            <WarehouseTable
              items={items}
              onUpdateQuantity={handleUpdateQuantity}
              onRename={handleRename}
              onOpenMinQuantityModal={openMinQuantityModal}
              onDelete={setDeleteItem}
              onOpenHistory={openHistoryDrawer}
              onOpenQuantityModal={openQuantityModal}
            />
          </Box>
        </Flex>

      )}

      <WarehouseFormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        onSubmit={handleCreate}
      />

      <WarehouseHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={onHistoryClose}
        item={historyItem}
      />

      <ModalInfo
        title='Conferma eliminazione'
        body={
          deleteItem
            ? `Sei sicuro di voler eliminare il prodotto "${deleteItem.name}"?`
            : ''
        }
        isOpen={!!deleteItem}
        onCancel={() => setDeleteItem(null)}
        onClose={handleDeleteConfirmed}
      />

      <ModalInfo
        title='Risultato eliminazione'
        body={deleteResultMessage}
        isOpen={isDeleteResultOpen}
        onClose={() => setIsDeleteResultOpen(false)}
      />

      <WarehouseQuantityModal
        isOpen={isQuantityOpen}
        onClose={() => {
          setQuantityItem(null);
          onQuantityClose();
        }}
        item={quantityItem}
        actionType={quantityActionType}
        onConfirm={handleConfirmQuantityChange}
      />

      <WarehouseMinQuantityModal
        isOpen={isMinQuantityOpen}
        onClose={() => {
          setMinQuantityItem(null);
          onMinQuantityClose();
        }}
        item={minQuantityItem}
        onConfirm={handleConfirmMinQuantityChange}
      />
    </VStack>
  );
};

export default WarehousePage;

